import { useFormikContext } from 'formik';
import { useState, useCallback } from 'react';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  Empty,
  EmptyHeadline,
  FormControl,
  FormLabel,
  IconButton,
  Popover,
  PopoverBody,
  PopoverButtonGroup,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Portal,
  EmptyDrawerIcon,
  Flex,
} from '@frontend/components';
import { SwapVerticalIcon } from '@frontend/components';

import { featureFlagsStaticData } from 'modules/common/drawers/featureFlagDrawer/const/featureFlags';
import {
  FeatureFlagAvailability,
  FeatureFlagCategory,
  FeatureFlagType,
} from 'modules/common/drawers/featureFlagDrawer/types';
import type { FeatureFlagFormData } from 'modules/common/drawers/featureFlagDrawer/types';

import {
  getFlagsForCategory,
  getTierDefaultValue,
  getUpsellFlagsForTier,
  type Tier,
} from './utils';

export type TierAlignmentPopoverProps = {
  category: FeatureFlagCategory;
};

export const TierAlignmentPopover = ({
  category,
}: TierAlignmentPopoverProps) => {
  const { setFieldValue, values } = useFormikContext<FeatureFlagFormData>();
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [selectedUpsells, setSelectedUpsells] = useState<Set<string>>(
    new Set(),
  );
  const [includeContracted, setIncludeContracted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Reset all popover state on close
    setSelectedTier(null);
    setSelectedUpsells(new Set());
    setIncludeContracted(false);
  }, []);

  const handleTierChange = useCallback(
    (tier: Tier | null) => {
      setSelectedTier(tier);
      // Initialize upsells based on current form values when tier changes
      if (tier) {
        const upsellFlagsForTier = getUpsellFlagsForTier(tier, category);
        const currentlyEnabledUpsells = new Set<string>();
        upsellFlagsForTier.forEach((flagKey) => {
          const currentValue = values[flagKey as keyof FeatureFlagFormData];
          // Pre-check if the flag is currently true in the form
          if (currentValue === true) {
            currentlyEnabledUpsells.add(flagKey);
          }
        });
        setSelectedUpsells(currentlyEnabledUpsells);
      } else {
        setSelectedUpsells(new Set());
      }
    },
    [category, values],
  );

  const handleUpsellChange = useCallback((values: string[]) => {
    setSelectedUpsells(new Set(values));
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedTier(null);
    setSelectedUpsells(new Set());
    setIncludeContracted(false);
    handleClose();
  }, [handleClose]);

  const handleSubmit = useCallback(() => {
    if (!selectedTier) return;

    const flagsInCategory = getFlagsForCategory(category);

    // Set all boolean flags in category to their tier defaults
    // Only process boolean flags with CheckEntitlement availability
    flagsInCategory.forEach((flagKey) => {
      const flagData =
        featureFlagsStaticData[flagKey as keyof typeof featureFlagsStaticData];
      if (!flagData) return;

      // Only process boolean flags with CheckEntitlement availability
      if (
        flagData.type !== FeatureFlagType.Boolean ||
        flagData.availability !== FeatureFlagAvailability.CheckEntitlement
      ) {
        return;
      }

      // If contracted flag and not including contracted, set to false
      if (!includeContracted && flagData.contracted) {
        setFieldValue(flagKey, false);
        return;
      }

      const tierDefault = getTierDefaultValue(flagData, selectedTier);

      // Only set if there's a default value defined
      if (tierDefault !== undefined) {
        setFieldValue(flagKey, tierDefault);
      }
    });

    // For selected upsells, ensure they're enabled (only boolean flags with CheckEntitlement availability)
    selectedUpsells.forEach((flagKey) => {
      const flagData =
        featureFlagsStaticData[flagKey as keyof typeof featureFlagsStaticData];
      if (!flagData) return;

      // Only process boolean flags with CheckEntitlement availability
      if (
        flagData.type !== FeatureFlagType.Boolean ||
        flagData.availability !== FeatureFlagAvailability.CheckEntitlement
      ) {
        return;
      }

      // Enable the boolean feature
      setFieldValue(flagKey, true);
    });

    // Reset form state and close popover
    setSelectedTier(null);
    setSelectedUpsells(new Set());
    setIncludeContracted(false);
    handleClose();
  }, [
    category,
    selectedTier,
    selectedUpsells,
    includeContracted,
    setFieldValue,
    handleClose,
  ]);

  const upsellFlags = selectedTier
    ? getUpsellFlagsForTier(selectedTier, category)
    : [];

  // Initialize upsells when popover opens if tier is already selected
  const handleOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(true);
      // If tier is already selected, initialize upsells from current form values
      if (selectedTier) {
        const upsellFlagsForTier = getUpsellFlagsForTier(
          selectedTier,
          category,
        );
        const currentlyEnabledUpsells = new Set<string>();
        upsellFlagsForTier.forEach((flagKey) => {
          const currentValue = values[flagKey as keyof FeatureFlagFormData];
          if (currentValue === true) {
            currentlyEnabledUpsells.add(flagKey);
          }
        });
        setSelectedUpsells(currentlyEnabledUpsells);
      }
    },
    [selectedTier, category, values],
  );

  const tierOptions = [
    { label: 'Growth', value: 'growth' },
    { label: 'Pro', value: 'pro' },
    { label: 'Enterprise', value: 'ent' },
    { label: 'Enterprise Multi-site', value: 'entMs' },
  ];

  const selectedTierOption = selectedTier
    ? tierOptions.find((opt) => opt.value === selectedTier) || null
    : null;

  return (
    <Popover
      isOpen={isOpen}
      onClose={handleClose}
      placement="bottom"
      returnFocusOnClose={false}
      closeOnBlur={false}
      modifiers={[
        {
          name: 'flip',
          enabled: false,
        },
        {
          name: 'preventOverflow',
          enabled: true,
        },
      ]}
    >
      <PopoverTrigger>
        <IconButton
          aria-label="Align tier and select upsells"
          size="lg"
          variant="ghost"
          onClick={handleOpen}
        >
          <SwapVerticalIcon />
        </IconButton>
      </PopoverTrigger>
      <Portal>
        <PopoverContent>
          <PopoverHeader textTransform="none">
            Align entitlements with tier
          </PopoverHeader>
          <PopoverBody display="flex" flexDirection="column" gap={2}>
            <Flex direction="row" align="center" gap={2}>
              <FormControl>
                <FormLabel>Pricing tier</FormLabel>
                <Select
                  value={selectedTierOption}
                  onChange={(option: { label: string; value: Tier } | null) => {
                    if (option) {
                      handleTierChange(option.value);
                    } else {
                      handleTierChange(null);
                    }
                  }}
                  options={tierOptions}
                  isClearable={false}
                  placeholder="Select a tier"
                />
              </FormControl>
              <FormControl>
                <FormLabel opacity={0} pointerEvents="none">
                  Contract status
                </FormLabel>
                <Checkbox
                  isChecked={includeContracted}
                  onChange={(e) => setIncludeContracted(e.target.checked)}
                >
                  Contracted
                </Checkbox>
              </FormControl>
            </Flex>

            {selectedTier && upsellFlags.length > 0 && (
              <FormControl>
                <FormLabel>
                  Upsells for{' '}
                  {selectedTier === 'growth'
                    ? 'Growth'
                    : selectedTier === 'pro'
                    ? 'Pro'
                    : selectedTier === 'ent'
                    ? 'Enterprise'
                    : 'Enterprise Multi-site'}
                </FormLabel>
                <CheckboxGroup
                  value={Array.from(selectedUpsells)}
                  onChange={handleUpsellChange}
                >
                  {upsellFlags.map((flagKey) => {
                    const flagData =
                      featureFlagsStaticData[
                        flagKey as keyof typeof featureFlagsStaticData
                      ];
                    return (
                      <Checkbox key={flagKey} value={flagKey}>
                        {flagData?.displayName ?? flagKey}
                      </Checkbox>
                    );
                  })}
                </CheckboxGroup>
              </FormControl>
            )}

            {selectedTier && upsellFlags.length === 0 && (
              <Empty size="sm">
                <EmptyDrawerIcon />
                <EmptyHeadline>
                  No upsells available for this tier
                </EmptyHeadline>
              </Empty>
            )}
          </PopoverBody>
          <PopoverFooter>
            <PopoverButtonGroup>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                isDisabled={!selectedTier}
              >
                Apply
              </Button>
            </PopoverButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
